/**
 * airan-ntn — Handover Prediction xApp
 * Predicts UE handover needs based on signal quality, Doppler, elevation
 */

import { XAppBase } from './XAppBase.js';
import { E2KpmReport, E2RcAction, XAppDecision } from '../types.js';

export class HoPredictXApp extends XAppBase {
  private ueScores: Map<number, number> = new Map();

  constructor(enabled: boolean = true) {
    super('ho-predict', enabled);
  }

  processKpmReport(report: E2KpmReport): void {
    for (const ueReport of report.ueReports) {
      const score = this.computeHandoverScore(ueReport, report);
      this.ueScores.set(ueReport.ueId, score);
    }
  }

  private computeHandoverScore(ueReport: E2KpmReport['ueReports'][0], report: E2KpmReport): number {
    let score = 0;
    if (ueReport.sinr < 5) score += 30;
    if (ueReport.rsrp < -110) score += 25;
    if (Math.abs(report.dopplerShift) > 3000) score += 20;
    if (report.elevation < 15) score += 15;
    if (ueReport.harqNackRate > 0.1) score += 10;
    return Math.min(100, score);
  }

  decisionCycle(): XAppDecision {
    const start = performance.now();
    const actions: E2RcAction[] = [];

    for (const [ueId, score] of this.ueScores) {
      if (score > 70) {
        // Find best candidate satellite (simplified)
        actions.push({
          type: 'HANDOVER_TRIGGER',
          targetId: ueId,
          payload: { targetSatId: 0, reason: 'predicted_ho', confidence: score / 100 },
          priority: this.config.priority,
          timestamp: Date.now()
        });
      } else if (score > 40) {
        actions.push({
          type: 'HANDOVER_CANCEL',
          targetId: ueId,
          payload: { reason: 'score_below_threshold' },
          priority: this.config.priority,
          timestamp: Date.now()
        });
      }
    }

    const latency = performance.now() - start;
    this.recordDecision(latency);

    return { actions, confidence: 0.85, latencyMs: latency };
  }

  getRequiredSubscription(): { eventTriggers: string[]; reportStyle: string } {
    return {
      eventTriggers: this.config.requiredEventTriggers,
      reportStyle: this.config.requiredReportStyle
    };
  }
}